var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { ChevronLeft, ChevronRight } from '@shared/icons/icons';
import { getTimeDimensionName } from '@data/tag/tagUtil';
import { useTagContext } from '@data/tag/tagContext';
import { parseInterval } from '@util/helpers';
import { RangeSliderIndicator, RangeIndicatorThumbEnd, RangeSliderIndicatorThumbStart, RangeIndicatorThumbTooltip, RangeSliderTipLeft, RangeSliderTipRight, RangeSliderContainer, RangeInterval, RangeIntervalsContainer, YearButtonsContainer, YearButton, DateIntervalContainer, YearButtonWrapper, } from '@styled/DateIntervalSlider';
import CheckboxMenu from './CheckboxMenu';
import { createPortal } from 'react-dom';
import { useActiveVersions } from '@data/hooks/versions';
import { useActiveYears } from '@data/hooks/timePoints';
var THUMB_CLASS_NAME = 'date-range-thumb';
var THUMB_START_CLASS_NAME = 'date-range-thumb-start';
var THUMB_END_CLASS_NAME = 'date-range-thumb-end';
var MIN_STEP_WIDTH = 16;
function getIntervalTooltipLabels(toolTip, monthTags, yearTags) {
    var _a = __read(toolTip.split('/'), 2), ms = _a[0], ys = _a[1];
    var monthTag = monthTags.find(function (mt) { return mt.name === "M".concat(parseInt(ms)); });
    var yearTag = yearTags.find(function (yt) { return yt.name === "Y".concat(parseInt(ys)); });
    if (monthTag && yearTag) {
        ms = getTimeDimensionName(monthTag);
        ys = getTimeDimensionName(yearTag);
    }
    return "".concat(ms, "/").concat(ys);
}
function DateIntervalSlider(_a) {
    var _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q;
    var range = _a.range, start = _a.start, end = _a.end, steps = _a.steps, onIntervalChange = _a.onIntervalChange, yearVersions = _a.yearVersions, yearVersionToggle = _a.yearVersionToggle, rest = __rest(_a, ["range", "start", "end", "steps", "onIntervalChange", "yearVersions", "yearVersionToggle"]);
    var containerEl = useRef(null);
    var shouldStopClick = useRef(false);
    var thumbStartRef = useRef(null);
    var thumbEndRef = useRef(null);
    var selectedThumb = useRef(null);
    var movementStartClientX = useRef(null);
    var tagData = useTagContext().tagData;
    var versions = useActiveVersions();
    var years = useActiveYears();
    var activeYears = (_b = rest.activeYears) !== null && _b !== void 0 ? _b : years;
    var activeVersions = (_c = rest.activeVersions) !== null && _c !== void 0 ? _c : versions;
    var _r = __read(useState(range), 2), shownRange = _r[0], setShownRange = _r[1];
    useEffect(function () {
        if (range.length) {
            setShownRange(range);
        }
    }, [range]);
    var _s = __read(useState(function () { return (__assign(__assign({}, calculatePosition(shownRange, start, end, activeYears)), { show: { start: true, indicator: true, end: true } })); }), 2), positionData = _s[0], setPositionData = _s[1];
    useEffect(function () {
        if (activeYears.length) {
            setPositionData(__assign(__assign({}, calculatePosition(shownRange, start, end, activeYears)), { show: { start: true, indicator: true, end: true } }));
        }
    }, [activeYears]);
    var _t = __read(useState(false), 2), isScrollable = _t[0], setIsScrollable = _t[1];
    var _u = __read(useState(null), 2), tooltip = _u[0], setTooltip = _u[1];
    var monthTags = tagData.internalTagsMap.months;
    var yearTags = tagData.internalTagsMap.years;
    var canScrollLeft = shownRange[0] !== range[0];
    var canScrollRight = shownRange[shownRange.length - 1] !== range[range.length - 1];
    var handleResize = useDebouncedCallback(function () {
        var _a;
        if (containerEl && containerEl.current) {
            var containerWidth = (_a = containerEl.current) === null || _a === void 0 ? void 0 : _a.offsetWidth;
            var shownStepWidth = containerWidth * getStep(shownRange);
            var newShownRange = __spreadArray([], __read(shownRange), false);
            var newStepWidth = shownStepWidth;
            if (shownStepWidth > MIN_STEP_WIDTH && range.length > shownRange.length) {
                while (range.length > newShownRange.length && newStepWidth > MIN_STEP_WIDTH) {
                    newShownRange.push(newShownRange[newShownRange.length - 1] + 1);
                    newStepWidth = containerWidth * getStep(newShownRange);
                    if (newStepWidth < MIN_STEP_WIDTH) {
                        newShownRange.pop();
                        break;
                    }
                }
                setShownRange(newShownRange);
                setIsScrollable(range.length !== newShownRange.length);
            }
            if (shownStepWidth < MIN_STEP_WIDTH && shownRange.length > 1) {
                while (newStepWidth < MIN_STEP_WIDTH && newShownRange.length > 1) {
                    newShownRange.pop();
                }
                setShownRange(newShownRange);
                setIsScrollable(true);
            }
        }
    }, 150);
    //Initial resize
    useEffect(function () {
        if (containerEl && containerEl.current) {
            var shownStepWidth = containerEl.current.offsetWidth * getStep(range);
            var newShownRange = __spreadArray([], __read(range), false);
            var newStepWidth = shownStepWidth;
            if (shownStepWidth < MIN_STEP_WIDTH && range.length > 1) {
                while (newStepWidth < MIN_STEP_WIDTH && newShownRange.length > 1) {
                    newShownRange.pop();
                }
                setShownRange(newShownRange);
                setIsScrollable(true);
            }
        }
    }, [range]);
    // Handles element resize
    useLayoutEffect(function () {
        shouldStopClick.current = false;
        var observer = new ResizeObserver(handleResize);
        observer.observe(containerEl.current);
        return function () {
            observer.disconnect();
        };
    }, [handleResize]);
    useEffect(function () {
        shouldStopClick.current = false;
        var updatedPosition = calculatePosition(shownRange, start, end, activeYears);
        var _a = parseInterval(start, end, activeYears), startYear = _a.startYear, endYear = _a.endYear;
        var rangeStart = shownRange[0];
        var rangeEnd = shownRange[shownRange.length - 1];
        if ((rangeStart > endYear && rangeStart > startYear) || (rangeEnd < startYear && rangeEnd < endYear)) {
            setPositionData(__assign(__assign({}, updatedPosition), { show: { indicator: false, start: false, end: false } }));
        }
        if (rangeStart <= endYear && rangeStart > startYear) {
            setPositionData(__assign(__assign({}, updatedPosition), { left: '0%', show: { indicator: true, start: false, end: true } }));
        }
        if (rangeEnd >= startYear && rangeEnd < endYear) {
            setPositionData(__assign(__assign({}, updatedPosition), { right: '0%', show: { indicator: true, start: true, end: false } }));
        }
        if (rangeStart <= startYear && rangeEnd >= endYear) {
            setPositionData(__assign(__assign({}, updatedPosition), { show: { indicator: true, start: true, end: true } }));
        }
        if (rangeStart > startYear && rangeEnd < endYear) {
            setPositionData({ left: '0%', right: '0%', show: { indicator: true, start: false, end: false } });
        }
    }, [shownRange[0], shownRange[shownRange.length - 1], start, end]);
    //Tu nastaju komplikacije
    var mouseMoveHandler = useCallback(function (e) {
        var _a, _b;
        stopPropagationHandler(e);
        if (movementStartClientX.current === null) {
            return;
        }
        var movementX = e.clientX - movementStartClientX.current;
        movementStartClientX.current = e.clientX;
        var movementSpeed = movementX / ((_b = (_a = containerEl.current) === null || _a === void 0 ? void 0 : _a.offsetWidth) !== null && _b !== void 0 ? _b : 1);
        var initialIntervalLength = getIntervalLength(start, end, activeYears);
        setPositionData(function (current) {
            var newPosition = alignToStep(current, shownRange);
            var _a = getInterval(newPosition, shownRange), intervalStart = _a.intervalStart, intervalEnd = _a.intervalEnd;
            if (selectedThumb.current !== null) {
                setTooltip(selectedThumb.current === 'start' ? { start: intervalStart } : { end: intervalEnd });
                return __assign(__assign({}, resizeRange(current, movementSpeed, shownRange, selectedThumb.current)), { show: current.show });
            }
            setTooltip({ start: intervalStart, end: intervalEnd });
            if (!current.show.start && !current.show.end) {
                var globalPosition = calculatePosition(range, start, end, activeYears);
                var _b = getInterval(globalPosition, range), intervalStart_1 = _b.intervalStart, intervalEnd_1 = _b.intervalEnd;
                var _c = parseInterval(intervalStart_1, intervalEnd_1, activeYears), startYear_1 = _c.startYear, endYear_1 = _c.endYear;
                var updatedGlobalPosition = moveRange(globalPosition, movementSpeed, current.show);
                var shownPosition = current;
                if (toRatio(updatedGlobalPosition.right) < 0 || toRatio(updatedGlobalPosition.left) < 0) {
                    updatedGlobalPosition = calculatePosition(range, start, end, activeYears);
                }
                if (shownRange[0] <= startYear_1 && toRatio(updatedGlobalPosition.right) >= 0) {
                    return __assign(__assign({}, current), { show: __assign(__assign({}, current.show), { start: true }) });
                }
                if (shownRange[shownRange.length - 1] >= endYear_1 && toRatio(updatedGlobalPosition.left) >= 0) {
                    return __assign(__assign({}, current), { show: __assign(__assign({}, current.show), { end: true }) });
                }
                return __assign(__assign({}, shownPosition), { global: updatedGlobalPosition });
            }
            var newIntervalLength;
            if ((!current.show.start && current.show.end) || (!current.show.end && current.show.start))
                newIntervalLength = getIntervalLength(intervalStart, intervalEnd, activeYears);
            if (newIntervalLength && newIntervalLength >= initialIntervalLength) {
                var globalPosition = calculatePosition(range, intervalStart, intervalEnd, activeYears);
                var updatedGlobalPosition = moveRange(globalPosition, movementSpeed, current.show);
                var shown = { indicator: true, start: true, end: true };
                if (toRatio(updatedGlobalPosition.right) < 0 || toRatio(updatedGlobalPosition.left) < 0) {
                    return current;
                }
                return __assign(__assign({}, moveRange(current, movementSpeed, shown)), { show: shown });
            }
            return __assign(__assign({}, moveRange(current, movementSpeed, current.show)), { show: current.show });
        });
    }, [shownRange, positionData, start, end]);
    var mouseUpHandler = useCallback(function (e) {
        stopPropagationHandler(e);
        shouldStopClick.current = true;
        document.removeEventListener('mousemove', mouseMoveHandler);
        setPositionData(function (current) {
            var newPosition = alignToStep(current, shownRange);
            var _a = getInterval(newPosition, shownRange), intervalStart = _a.intervalStart, intervalEnd = _a.intervalEnd;
            var updatedStart = intervalStart;
            var updatedEnd = intervalEnd;
            var stepDiff;
            if (!current.show.start && current.show.end) {
                stepDiff = getIntervalLength(end, intervalEnd, activeYears);
                updatedStart = addSteps(range, start, stepDiff);
            }
            if (!current.show.end && current.show.start) {
                stepDiff = getIntervalLength(intervalStart, start, activeYears);
                updatedEnd = addSteps(range, end, -stepDiff);
            }
            if (!current.show.end && !current.show.start) {
                var _b = getInterval(current.global, range), intervalStart_2 = _b.intervalStart, intervalEnd_2 = _b.intervalEnd;
                onIntervalChange(intervalStart_2, intervalEnd_2);
                return current;
            }
            if (selectedThumb.current === 'start')
                updatedEnd = end;
            if (selectedThumb.current === 'end')
                updatedStart = start;
            onIntervalChange(updatedStart, updatedEnd);
            return __assign(__assign({}, newPosition), { show: current.show });
        });
        setTooltip(null);
        document.removeEventListener('mouseup', mouseUpHandler);
    }, [shownRange, range, start, end, mouseMoveHandler, onIntervalChange]);
    function mouseDownHandler(e) {
        stopPropagationHandler(e);
        movementStartClientX.current = e.clientX;
        var isThumb = e.target.className.includes(THUMB_CLASS_NAME);
        selectedThumb.current = null;
        if (isThumb)
            selectedThumb.current = e.target.className.includes(THUMB_END_CLASS_NAME)
                ? 'end'
                : 'start';
        document.addEventListener('mousemove', mouseMoveHandler);
        document.addEventListener('mouseup', mouseUpHandler);
    }
    function mouseClickHandler(e) {
        if (shouldStopClick.current)
            return;
        var step = getStep(shownRange);
        var _a = e.target.getBoundingClientRect(), x = _a.x, width = _a.width;
        var elementX = e.clientX - x;
        var clickedRatio = elementX / width;
        var interval;
        isScrollable
            ? (interval = shiftScrolableRange(shownRange, range, start, end, activeYears, clickedRatio, step))
            : (interval = shiftRange(positionData, shownRange, clickedRatio, step));
        var intervalStart = interval.intervalStart, intervalEnd = interval.intervalEnd;
        onIntervalChange(intervalStart, intervalEnd);
    }
    function scrollClickHandler(e, side) {
        stopPropagationHandler(e);
        if (side === 'right') {
            setShownRange(function (current) {
                var startIndex = range.indexOf(current[0]) + 1;
                var endIndex = range.indexOf(current[current.length - 1]) + 2;
                var newRange = range.slice(startIndex, endIndex);
                return newRange;
            });
        }
        if (side === 'left') {
            setShownRange(function (current) {
                var startIndex = range.indexOf(current[0]) - 1;
                var endIndex = range.indexOf(current[current.length - 1]);
                var newRange = range.slice(startIndex, endIndex);
                return newRange;
            });
        }
    }
    function mouseEnterHandler(e, thumb) {
        var _a = getInterval(positionData, shownRange), intervalStart = _a.intervalStart, intervalEnd = _a.intervalEnd;
        stopPropagationHandler(e);
        if (thumb === null)
            return setTooltip({ start: intervalStart, end: intervalEnd });
        if (thumb === 'start')
            return setTooltip({ start: intervalStart });
        if (thumb === 'end')
            return setTooltip({ end: intervalEnd });
    }
    function mouseLeaveHandler() {
        setTooltip(null);
    }
    function yearClickHandler(year) {
        onIntervalChange("01/".concat(year), "12/".concat(year));
    }
    function stopPropagationHandler(e) {
        e.preventDefault();
        e.stopPropagation();
    }
    var rangeIntervals = useMemo(function () {
        var shownIntervals = shownRange.reduce(function (elementArray, year) {
            for (var s = 1; s <= 12; s++) {
                elementArray.push(React.createElement(RangeInterval, { key: "".concat(s, "/").concat(year), "$hidden": !!(s % steps), "$thicc": s === 1 }));
            }
            return elementArray;
        }, []);
        shownIntervals.push(React.createElement(RangeInterval, { key: "".concat(12, "/").concat(shownRange[shownRange.length - 1], "-end"), "$thicc": true }));
        return shownIntervals;
    }, [shownRange]);
    var _v = parseInterval(start, end, activeYears), startYear = _v.startYear, endYear = _v.endYear;
    var yearButtons = shownRange.map(function (y) {
        var _a, _b;
        var yearTag = yearTags.find(function (yt) { return yt.name === "Y".concat(y); });
        var yearInRange = y >= startYear && y <= endYear;
        return (React.createElement(YearButtonWrapper, { key: y },
            React.createElement(YearButton, { "$inRange": yearInRange, onClick: function () { return yearClickHandler(y); } }, yearTag ? getTimeDimensionName(yearTag) : y),
            yearInRange && yearVersionToggle && (React.createElement(CheckboxMenu, { items: activeVersions.map(function (v) {
                    var _a, _b;
                    return ({
                        label: v.name,
                        checked: (_b = (_a = yearVersions[y]) === null || _a === void 0 ? void 0 : _a.includes(v.id)) !== null && _b !== void 0 ? _b : false,
                        onToggle: function () { return yearVersionToggle(y, v.id); },
                    });
                }), active: ((_b = (_a = yearVersions[y]) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0) > 0 }))));
    });
    var minTooltip = tooltip && tooltip.start && (React.createElement(RangeIndicatorThumbTooltip, { "$thumbTop": (_e = (_d = thumbStartRef.current) === null || _d === void 0 ? void 0 : _d.getBoundingClientRect().top) !== null && _e !== void 0 ? _e : 0, "$thumbRight": (_g = (_f = thumbStartRef.current) === null || _f === void 0 ? void 0 : _f.getBoundingClientRect().right) !== null && _g !== void 0 ? _g : 0 },
        React.createElement("h5", null, getIntervalTooltipLabels(tooltip.start, monthTags, yearTags))));
    var memoizedMinTooltip = useMemo(function () { return createPortal(minTooltip, document.getElementById('portal')); }, [
        tooltip === null || tooltip === void 0 ? void 0 : tooltip.start,
        (_h = thumbStartRef.current) === null || _h === void 0 ? void 0 : _h.getBoundingClientRect().top,
        (_j = thumbStartRef.current) === null || _j === void 0 ? void 0 : _j.getBoundingClientRect().right,
    ]);
    var maxTooltip = (tooltip === null || tooltip === void 0 ? void 0 : tooltip.end) && (React.createElement(RangeIndicatorThumbTooltip, { "$thumbTop": (_l = (_k = thumbEndRef.current) === null || _k === void 0 ? void 0 : _k.getBoundingClientRect().top) !== null && _l !== void 0 ? _l : 0, "$thumbRight": (_o = (_m = thumbEndRef.current) === null || _m === void 0 ? void 0 : _m.getBoundingClientRect().right) !== null && _o !== void 0 ? _o : 0 },
        React.createElement("h5", null, getIntervalTooltipLabels(tooltip.end, monthTags, yearTags))));
    var memoizedMaxTooltip = useMemo(function () { return createPortal(maxTooltip, document.getElementById('portal')); }, [
        tooltip === null || tooltip === void 0 ? void 0 : tooltip.end,
        (_p = thumbEndRef.current) === null || _p === void 0 ? void 0 : _p.getBoundingClientRect().top,
        (_q = thumbEndRef.current) === null || _q === void 0 ? void 0 : _q.getBoundingClientRect().right,
    ]);
    return (React.createElement(DateIntervalContainer, { ref: containerEl, className: 'unselectable' },
        React.createElement("div", null,
            React.createElement(RangeSliderContainer, { onClick: mouseClickHandler },
                isScrollable && canScrollLeft && (React.createElement(RangeSliderTipLeft, { onClick: function (e) { return scrollClickHandler(e, 'left'); } },
                    React.createElement(ChevronLeft, { cursor: "pointer" }))),
                positionData.show.indicator && (React.createElement(RangeSliderIndicator, { onMouseDown: mouseDownHandler, onClick: stopPropagationHandler, onMouseOver: function (e) { return mouseEnterHandler(e, null); }, onMouseOut: mouseLeaveHandler, style: positionData },
                    positionData.show.start && (React.createElement(RangeSliderIndicatorThumbStart, { ref: thumbStartRef, onClick: stopPropagationHandler, onMouseOver: function (e) { return mouseEnterHandler(e, 'start'); }, onMouseOut: mouseLeaveHandler, className: "".concat(THUMB_CLASS_NAME, " ").concat(THUMB_START_CLASS_NAME) }, memoizedMinTooltip)),
                    positionData.show.end && (React.createElement(RangeIndicatorThumbEnd, { ref: thumbEndRef, onClick: stopPropagationHandler, onMouseOver: function (e) { return mouseEnterHandler(e, 'end'); }, onMouseOut: mouseLeaveHandler, className: "".concat(THUMB_CLASS_NAME, " ").concat(THUMB_END_CLASS_NAME) }, memoizedMaxTooltip)))),
                isScrollable && canScrollRight && (React.createElement(RangeSliderTipRight, { onClick: function (e) { return scrollClickHandler(e, 'right'); } },
                    React.createElement(ChevronRight, { cursor: "pointer" }))))),
        React.createElement(RangeIntervalsContainer, null, rangeIntervals),
        React.createElement(YearButtonsContainer, null, yearButtons)));
}
function shiftRange(position, shownRange, clickedRatio, step) {
    var _a = positionToRatios(position), leftRatio = _a.leftRatio, rightRatio = _a.rightRatio;
    var intervalRange = 1 - rightRatio - leftRatio;
    var intervalHalf = intervalRange / 2;
    var offset;
    var leftRatioCalc = 0;
    var rightRatioCalc = 0;
    if (clickedRatio > leftRatio) {
        offset = clickedRatio - (1 - rightRatio) + intervalHalf;
        leftRatioCalc = leftRatio + offset;
        rightRatioCalc = rightRatio - offset;
    }
    if (clickedRatio < leftRatio) {
        offset = leftRatio - clickedRatio + intervalHalf;
        leftRatioCalc = leftRatio - offset;
        rightRatioCalc = rightRatio + offset;
    }
    if (leftRatioCalc < 0) {
        leftRatioCalc = 0;
        rightRatioCalc = 1 - intervalRange;
    }
    if (rightRatioCalc < 0) {
        rightRatioCalc = 0;
        leftRatioCalc = 1 - intervalRange;
    }
    var newPosition = toAlignedPosition(leftRatioCalc, rightRatioCalc, step);
    return getInterval(newPosition, shownRange);
}
function shiftScrolableRange(shownRange, range, start, end, activeYears, clickedRatio, step) {
    var intervalLength = getIntervalLength(start, end, activeYears);
    var intervalHalf = intervalLength / 2;
    var clickedStep = clickedRatio / step;
    var startYearIdx = toYearIdx(Math.round(clickedStep - intervalHalf));
    var endYearIdx = toYearIdx(Math.round(clickedStep + intervalHalf));
    var monthStart = toMonth(Math.round(clickedStep - intervalHalf), startYearIdx);
    var monthEnd = toMonth(Math.round(clickedStep + intervalHalf), endYearIdx);
    var yearStart;
    var yearEnd;
    if (startYearIdx >= 0)
        yearStart = shownRange[startYearIdx];
    if (endYearIdx <= shownRange.length - 1)
        yearEnd = shownRange[endYearIdx];
    if (startYearIdx < 0) {
        var yearIdx = range.indexOf(shownRange[0]) + startYearIdx;
        yearStart = range[yearIdx];
    }
    if (endYearIdx > shownRange.length - 1) {
        var yearIdx = range.indexOf(shownRange[shownRange.length - 1]) + (endYearIdx - (shownRange.length - 1));
        yearEnd = range[yearIdx];
    }
    if (!yearStart) {
        yearStart = range[0];
        monthStart = 1;
        var yearIdx = toYearIdx(intervalLength);
        monthEnd = toMonth(intervalLength, yearIdx);
        yearEnd = range[yearIdx];
    }
    if (!yearEnd) {
        yearEnd = range[range.length - 1];
        monthEnd = 12;
        var monthIdx = range.length * 12 - (intervalLength + 1);
        var yearIdx = toYearIdx(monthIdx);
        monthStart = toMonth(monthIdx, yearIdx);
        yearStart = range[yearIdx];
    }
    return toDates(monthStart, yearStart, monthEnd, yearEnd);
}
function getInterval(position, range) {
    var step = getStep(range);
    var maxSteps = getMaxSteps(range);
    var _a = positionToRatios(position), leftRatio = _a.leftRatio, rightRatio = _a.rightRatio;
    var _b = ratiosToSteps(leftRatio, rightRatio, step), leftStep = _b.leftStep, rightStep = _b.rightStep;
    var startIdx = leftStep;
    var endIdx = maxSteps - rightStep;
    var _c = idxsToYearIdxs(startIdx, endIdx), startYearIdx = _c.startYearIdx, endYearIdx = _c.endYearIdx;
    var _d = toMonths(startIdx, startYearIdx, endIdx, endYearIdx), startMonth = _d.startMonth, endMonth = _d.endMonth;
    return toDates(startMonth, range[startYearIdx], endMonth, range[endYearIdx]);
}
function addSteps(range, date, step) {
    var _a = __read(parseDate(date), 2), month = _a[0], year = _a[1];
    var targetIdx = toIdx(range.indexOf(year), month) + step;
    var targetYearIdx = toYearIdx(targetIdx);
    var targetYear = range[targetYearIdx];
    var targetMonth = toMonth(targetIdx, targetYearIdx);
    return formatDate(targetMonth, targetYear);
}
function parseDate(date) {
    return date.split('/').map(function (val) { return parseInt(val); });
}
function getIntervalLength(start, end, activeYears) {
    var _a = parseInterval(start, end, activeYears), startMonth = _a.startMonth, startYear = _a.startYear, endMonth = _a.endMonth, endYear = _a.endYear;
    return endMonth - startMonth + (endYear - startYear) * 12;
}
function alignToStep(position, range) {
    var step = getStep(range);
    var _a = positionToRatios(position), leftRatio = _a.leftRatio, rightRatio = _a.rightRatio;
    return toAlignedPosition(leftRatio, rightRatio, step);
}
function calculatePosition(range, start, end, activeYears) {
    var step = getStep(range);
    var maxSteps = getMaxSteps(range);
    var _a = parseInterval(start, end, activeYears), startMonth = _a.startMonth, startYear = _a.startYear, endMonth = _a.endMonth, endYear = _a.endYear;
    var startYearIdx = range.indexOf(startYear);
    var endYearIdx = range.indexOf(endYear);
    var _b = toIdxs(startYearIdx, startMonth, endYearIdx, endMonth), startIdx = _b.startIdx, endIdx = _b.endIdx;
    var leftStep = startIdx;
    var rightStep = maxSteps - endIdx;
    var leftRatio = leftStep * step;
    var rightRatio = rightStep * step;
    return ratiosToPositon(leftRatio, rightRatio);
}
function moveRange(currentPosition, movement, show) {
    var _a = calculateMovement(currentPosition, movement), leftRatio = _a.leftRatio, rightRatio = _a.rightRatio;
    if ((leftRatio < 0 || rightRatio < 0) && show.start && show.end) {
        return currentPosition;
    }
    if (!show.start && show.end) {
        return __assign(__assign({}, currentPosition), { right: toPercentage(rightRatio) });
    }
    if (!show.end && show.start) {
        return __assign(__assign({}, currentPosition), { left: toPercentage(leftRatio) });
    }
    return ratiosToPositon(leftRatio, rightRatio);
}
function calculateMovement(currentPosition, movement) {
    var _a = positionToRatios(currentPosition), leftRatio = _a.leftRatio, rightRatio = _a.rightRatio;
    return {
        leftRatio: leftRatio + movement,
        rightRatio: rightRatio - movement,
    };
}
function resizeRange(currentPosition, movement, range, thumb) {
    var step = getStep(range);
    var left = currentPosition.left, right = currentPosition.right;
    var _a = calculateMovement(currentPosition, movement), leftRatio = _a.leftRatio, rightRatio = _a.rightRatio;
    var intervalLength = 1 - rightRatio - leftRatio;
    var resizedRange = __assign({}, currentPosition);
    var stepDiff = null;
    if (intervalLength < step)
        stepDiff = step - intervalLength;
    if (thumb === 'start')
        resizedRange.left = resizeStart(left, leftRatio, movement, stepDiff);
    if (thumb === 'end')
        resizedRange.right = resizeEnd(right, rightRatio, movement, stepDiff);
    return resizedRange;
}
function resizeEnd(right, rightRatio, movement, stepDiff) {
    if (stepDiff && movement < 0)
        return toPercentage(toRatio(right) - stepDiff);
    if (rightRatio >= 0)
        return toPercentage(rightRatio);
    return right;
}
function resizeStart(left, leftRatio, movement, stepDiff) {
    if (stepDiff && movement > 0)
        return toPercentage(toRatio(left) - stepDiff);
    if (leftRatio >= 0)
        return toPercentage(leftRatio);
    return left;
}
//Helpers
function getStep(range) {
    return 1 / getMaxSteps(range);
}
function getMaxSteps(range) {
    return range.length * 12;
}
function formatDate(month, year) {
    return "".concat("".concat(month).length === 2 ? month : "0".concat(month), "/").concat(year);
}
function toPercentage(value) {
    return "".concat(value * 100, "%");
}
function toRatio(value) {
    var _a;
    return parseFloat((_a = value.replace) === null || _a === void 0 ? void 0 : _a.call(value, '%', '')) / 100;
}
function toStep(value, step) {
    return Math.round(value / step);
}
function toYearIdx(idx) {
    return Math.floor(idx / 12);
}
function toIdx(yearIdx, month) {
    return yearIdx * 12 - 1 + month;
}
function toMonth(idx, yearIdx) {
    return idx - yearIdx * 12 + 1;
}
function toDates(startMonth, startYear, endMonth, endYear) {
    return { intervalStart: formatDate(startMonth, startYear), intervalEnd: formatDate(endMonth, endYear) };
}
function toIdxs(startYearIdx, startMonth, endYearIdx, endMonth) {
    return { startIdx: toIdx(startYearIdx, startMonth), endIdx: toIdx(endYearIdx, endMonth + 1) };
}
function idxsToYearIdxs(startIdx, endIdx) {
    return { startYearIdx: toYearIdx(startIdx), endYearIdx: toYearIdx(endIdx - 1) };
}
function toMonths(startIdx, startYearIdx, endIdx, endYearIdx) {
    return { startMonth: toMonth(startIdx, startYearIdx), endMonth: toMonth(endIdx - 1, endYearIdx) };
}
function ratiosToSteps(leftRatio, rightRatio, step) {
    return { leftStep: toStep(leftRatio, step), rightStep: toStep(rightRatio, step) };
}
function positionToRatios(position) {
    var left = position.left, right = position.right;
    return { leftRatio: toRatio(left), rightRatio: toRatio(right) };
}
function ratiosToPositon(leftRatio, rightRatio) {
    return { left: toPercentage(leftRatio), right: toPercentage(rightRatio) };
}
function toAlignedPosition(leftRatio, rightRatio, step) {
    var _a = ratiosToSteps(leftRatio, rightRatio, step), leftStep = _a.leftStep, rightStep = _a.rightStep;
    return ratiosToPositon(leftStep * step, rightStep * step);
}
export default DateIntervalSlider;
